import { RecordLogLevel } from "graphql/generated/graphql";
import { getListFromGraphql } from "utils/utils";

const initialRowsPerPage = 10;
const initialOrder = "-created_at";

// How many rows the gradual Logs-tab read fetches per load gesture (initial load
// and each scroll-driven "load older"). The legacy pagination default of 10 was
// far too small for the un-paginated endless-scroll list — a scroll-to-bottom
// fetched only ~10 more rows, so the list barely advanced (Stefan, 2026-10-13).
// A screenful's worth loads smoothly; the server still keyset-pages under its own
// count-mode cap, and leg-B windowing keeps the mounted <tr> count bounded
// regardless of how many rows accumulate. See
// operator-task-fe-logs-tab-bounded-dom-virtualization.
export const gradualPageSize = 100;

export const initialQuery = {
    limit: initialRowsPerPage as typeof initialRowsPerPage,
    offset: 0,
    order: initialOrder,
};

// Workspace-config key + fallback for how many log rows the Logs tab keeps in
// browser memory. Backed by the `LOGS_MAX_IN_MEMORY_ROWS` User-Interface config
// (cloudomation/workspace/configuration.py); the FE falls back to this default
// when the key is absent (e.g. before the backend that registers it deploys).
// Live updates and gradual older-loading accumulate rows unbounded otherwise,
// which also shrinks the virtual-list scrollbar handle to an unusable sliver
// (Stefan, 2026-10-15). See operator-task-fe-logs-tab-bounded-dom-virtualization.
export const LOGS_MAX_IN_MEMORY_ROWS_KEY = "LOGS_MAX_IN_MEMORY_ROWS";
export const LOGS_MAX_IN_MEMORY_ROWS_DEFAULT = 2000;

export const logListPathFragment = "log-list";

// localStorage tableSettings key for the record-log list, so page size / sort
// order persist across navigation and reload (parity with other record lists).
export const recordLogListKey = "recordLogList";

// Severity-ascending display order (Info -> Warning -> Error): matches how a
// reader scans severity, least -> most severe, and is the order Stefan wants on
// the Level filter dropdown. The generated RecordLogLevel enum is alphabetical
// (Error, Info, Warning), so we re-order it here — the one list every logs
// screen (workspace / project / bundle) builds the Level control from, so the
// fix lands in a single place. Any unknown/new backend level keeps its position
// after the known ones, so adding a level never silently drops it from the
// filter. Stefan, 2026-10-15 (operator-task-fe-log-level-dropdown-reorder-and-color).
const LOG_LEVEL_SEVERITY_ORDER: RecordLogLevel[] = ["INFO", "WARNING", "ERROR"];

export const getRecordLogLevelList: () => RecordLogLevel[] = () => {
    const levels = getListFromGraphql("RecordLogLevel") as RecordLogLevel[];
    const rank = (level: RecordLogLevel): number => {
        const index = LOG_LEVEL_SEVERITY_ORDER.indexOf(level);
        return index === -1 ? LOG_LEVEL_SEVERITY_ORDER.length : index;
    };
    return [...levels].sort((a, b) => rank(a) - rank(b));
};

// Per-option className for the Level filter dropdown: Warning and Error carry a
// severity tint that reuses the SAME colours as the log rows (RecordTable.scss
// tr.warning / tr.error — styled in LogListFilter.scss). Info (and any
// unknown/new backend level) returns undefined, so it renders as normal text
// with no colour — matching how an "info" record_log row is unstyled.
// Stefan, 2026-10-16 (operator-task-fe-log-level-dropdown-reorder-and-color).
export const getLogLevelOptionClassName = (level: string): string | undefined => {
    switch (level.toUpperCase()) {
        case "WARNING":
            return "log-level-option log-level-option--warning";
        case "ERROR":
            return "log-level-option log-level-option--error";
        default:
            return undefined;
    }
};

// Static RecordTable options of the Logs list (single-record Logs tab and the
// workspace / project / bundle logs screens, both via LogsListing).
//  - `pagination: false`: one accumulating endless-scroll list, so there is no
//    pagination footer (no rows-per-page, no "n–m of x", no jump-to-page).
//    See operator-task-fe-logs-tab-gradual-load-day-by-day-no-pagination.
//  - `stickyHorizontalScrollbar: true`: a wide log table (many columns, long
//    Subject/Data cells) keeps a horizontal scrollbar within reach while the
//    user reads the top of a long list. The floating bar does not depend on the
//    pagination footer: without a footer it pins to the viewport bottom and
//    hands over to the table's own scrollbar once that scrolls into view, so
//    the user sees one horizontal scrollbar.
//    See operator-task-fe-logs-tab-restore-sticky-horizontal-scrollbar.
//  - `windowedBody: true`: only near-viewport rows are mounted (every log row
//    has one fixed height). See operator-task-fe-logs-tab-bounded-dom-virtualization.
export const logsTableBaseOptions = {
    pagination: false,
    stickyHorizontalScrollbar: true,
    windowedBody: true,
    selectableRows: "none" as const,
    // "controls" = right-side column selector without the left bulk-action
    // chrome (this list is selectableRows:"none").
    toolbar: "controls" as const,
    showDensityToggle: false,
    toolbarFilters: false,
    showSearch: false,
    viewColumns: true,
};
